// ── Ensemble breakdown: what each ensemble's members do with the system ──
// For every member of every ensemble set (GEFS, ECMWF ENS, AIFS, DeepMind):
// how strong it gets, and where it first makes landfall on the US Gulf or
// East Coast (or Puerto Rico) in the first BREAKDOWN_HOURS of its run.
// Counted per member, reported as a share of each ensemble, so a 31-member
// and a 1000-member ensemble read on the same scale. For a system the feed
// doesn't name yet (members matched by where their low forms), the share is
// of the WHOLE ensemble: a member that never forms it is a "no" everywhere.

import type { EnsembleSet } from "../data/liveApi";
import type { CoastlineDense } from "../data/replayApi";

export const BREAKDOWN_HOURS = 240; // each member's first 10 days
const STEP_DEG = 0.1; // tracks are sampled about every 11 km between forecast points
const NEAR_KM = 60; // within this of the coast, the coast's seaward side says land or sea
const CELL_DEG = 0.5;
const KM_PER_DEG = 111.2;

// US coast regions, west to east then north — rolled up from the
// coastline's named segments (the surge layer's), so counts stay readable
export const REGIONS = [
  "Texas", "Louisiana", "Mississippi / Alabama", "Florida Panhandle", "Florida Big Bend",
  "Florida west coast & Keys", "Florida east coast", "Georgia / S. Carolina", "North Carolina",
  "Mid-Atlantic", "New York / New England", "Puerto Rico",
] as const;
export type Region = (typeof REGIONS)[number];

const SEGMENT_REGION: Record<string, Region> = {
  "South Padre Island": "Texas", "Corpus Christi Bay": "Texas", "Matagorda Bay": "Texas", "Galveston Bay": "Texas",
  "Vermilion Bay": "Louisiana", "Terrebonne-Barataria": "Louisiana", "Grand Isle": "Louisiana",
  "SE Louisiana Coast": "Louisiana", "Lake Pontchartrain": "Louisiana",
  "Mississippi Sound": "Mississippi / Alabama", "Biloxi-Gulfport": "Mississippi / Alabama",
  "Mobile Bay": "Mississippi / Alabama", "Gulf Shores-Dauphin Island": "Mississippi / Alabama",
  "Pensacola Bay": "Florida Panhandle", "Destin-Fort Walton": "Florida Panhandle", "Panama City": "Florida Panhandle",
  "Big Bend FL": "Florida Big Bend",
  "Tampa Bay": "Florida west coast & Keys", "Charlotte Harbor": "Florida west coast & Keys",
  "Naples-Marco Island": "Florida west coast & Keys", "Florida Keys": "Florida west coast & Keys",
  "Miami-Dade": "Florida east coast", "Palm Beach-Treasure Coast": "Florida east coast",
  "Space Coast": "Florida east coast", "Jacksonville-St. Augustine": "Florida east coast",
  "Savannah-Hilton Head": "Georgia / S. Carolina", "Charleston": "Georgia / S. Carolina",
  "Outer Banks": "North Carolina",
  "Chesapeake Bay": "Mid-Atlantic", "Delaware Bay": "Mid-Atlantic", "New Jersey Shore": "Mid-Atlantic",
  "New York Harbor": "New York / New England", "Long Island Sound": "New York / New England",
};

/** The region a coastline point belongs to. Sabine Pass straddles the
 *  state line (93.84°W); Puerto Rico's segments all go to Puerto Rico. */
export function regionOf(name: string, lon: number): Region | null {
  if (name === "Sabine Pass") return lon < -93.84 ? "Texas" : "Louisiana";
  if (name.startsWith("Puerto Rico")) return "Puerto Rico";
  return SEGMENT_REGION[name] ?? null;
}

export interface CoastIndex {
  lat: number[];
  lon: number[];
  face: number[]; // seaward bearing, degrees (0 = N, 90 = E)
  region: (Region | null)[];
  cells: Map<number, number[]>;
  bbox: [number, number, number, number]; // s, w, n, e — grown by a cell, for a quick "nowhere near"
}

const cellKey = (row: number, col: number) => row * 4096 + col;

export function coastIndex(c: CoastlineDense): CoastIndex {
  const idx: CoastIndex = { lat: [], lon: [], face: [], region: [], cells: new Map(), bbox: [90, 180, -90, -180] };
  c.points.forEach(([lat, lon], i) => {
    idx.lat.push(lat);
    idx.lon.push(lon);
    idx.face.push(c.faces[i]);
    idx.region.push(regionOf(c.names[i] ?? "", lon));
    const k = cellKey(Math.floor(lat / CELL_DEG), Math.floor(lon / CELL_DEG));
    const list = idx.cells.get(k);
    if (list) list.push(i);
    else idx.cells.set(k, [i]);
    idx.bbox = [Math.min(idx.bbox[0], lat), Math.min(idx.bbox[1], lon), Math.max(idx.bbox[2], lat), Math.max(idx.bbox[3], lon)];
  });
  idx.bbox = [idx.bbox[0] - CELL_DEG, idx.bbox[1] - CELL_DEG, idx.bbox[2] + CELL_DEG, idx.bbox[3] + CELL_DEG];
  return idx;
}

/** Nearest coastline point within NEAR_KM, and whether (lat, lon) is on its
 *  land side; null when the coast is farther than that (open water, or land
 *  well inland — either way, not a landfall). */
function nearCoast(idx: CoastIndex, lat: number, lon: number): { i: number; land: boolean } | null {
  const [s, w, n, e] = idx.bbox;
  if (lat < s || lat > n || lon < w || lon > e) return null;
  const r = Math.floor(lat / CELL_DEG), c = Math.floor(lon / CELL_DEG);
  const k = Math.cos((lat * Math.PI) / 180);
  let best = -1, bestD = NEAR_KM;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      for (const i of idx.cells.get(cellKey(r + dr, c + dc)) ?? []) {
        const d = Math.hypot((lon - idx.lon[i]) * k, lat - idx.lat[i]) * KM_PER_DEG;
        if (d < bestD) { best = i; bestD = d; }
      }
    }
  }
  if (best < 0) return null;
  // bearing from the coast to the point vs the coast's seaward bearing
  const brg = (Math.atan2((lon - idx.lon[best]) * k, lat - idx.lat[best]) * 180) / Math.PI;
  const off = Math.abs((((brg - idx.face[best]) % 360) + 540) % 360 - 180);
  return { i: best, land: off > 90 };
}

export interface Landfall {
  region: Region;
  tau: number; // hours after the member's run started
  vmax: number; // kt, at landfall
}

type Pt = { tau: number; lat: number; lon: number; vmax: number };

/** A member's first landfall on the indexed coast within maxTau hours: the
 *  first sample on the land side of the coast after one just offshore (so a
 *  track drifting in overland, from Mexico say, is not a landfall). */
export function firstLandfall(points: Pt[], idx: CoastIndex, maxTau = BREAKDOWN_HOURS): Landfall | null {
  const pts = points.filter((p) => p.tau <= maxTau).sort((a, b) => a.tau - b.tau);
  let offshore = false;
  const visit = (p: Pt): Landfall | null => {
    const near = nearCoast(idx, p.lat, p.lon);
    if (!near) return null;
    if (!near.land) {
      offshore = true;
      return null;
    }
    const region = idx.region[near.i];
    return offshore && region ? { region, tau: Math.round(p.tau), vmax: Math.round(p.vmax) } : null;
  };
  for (let j = 0; j < pts.length; j++) {
    const a = pts[j];
    const b = pts[j + 1];
    if (!b) return visit(a);
    const dlon = ((b.lon - a.lon + 540) % 360) - 180;
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.lat - a.lat), Math.abs(dlon)) / STEP_DEG));
    for (let s = 0; s < n; s++) {
      const f = s / n;
      const hit = visit({
        tau: a.tau + (b.tau - a.tau) * f, lat: a.lat + (b.lat - a.lat) * f,
        lon: a.lon + dlon * f, vmax: a.vmax + (b.vmax - a.vmax) * f,
      });
      if (hit) return hit;
    }
  }
  return null;
}

export interface RegionCount {
  n: number; // members making their first landfall here
  hurricanes: number; // ... at hurricane strength (64 kt+) when they do
  taus: number[]; // when, hours after the run started
}

export interface SourceBreakdown {
  source: string;
  init: string; // the run (naive UTC)
  members: number; // the ensemble (every share's denominator)
  formed: number; // members carrying this system (fewer than `members` only for formation-matched sets)
  byFormation: boolean;
  ts: number; // members reaching tropical-storm strength (34 kt+) at some point
  hurricane: number; // 64 kt+
  major: number; // 96 kt+ (Cat 3+)
  landfall: Partial<Record<Region, RegionCount>>;
  noLandfall: number;
}

/** Per ensemble set: peak strength and first US landfall of every member in
 *  the first BREAKDOWN_HOURS of its run. Without a coastline, landfalls are
 *  left empty. */
export function ensembleBreakdown(sets: EnsembleSet[], coast: CoastIndex | null): SourceBreakdown[] {
  return sets.map((set) => {
    const formed = set.members.length;
    const out: SourceBreakdown = {
      source: set.source, init: set.init_time, members: Math.max(set.ens_size ?? formed, formed), formed,
      byFormation: set.matched_by === "formation",
      ts: 0, hurricane: 0, major: 0, landfall: {}, noLandfall: 0,
    };
    for (const m of set.members) {
      const pts = m.points.filter((p) => p.tau <= BREAKDOWN_HOURS);
      const peak = Math.max(0, ...pts.map((p) => p.vmax ?? 0));
      if (peak >= 34) out.ts++;
      if (peak >= 64) out.hurricane++;
      if (peak >= 96) out.major++;
      if (!coast) continue;
      const lf = firstLandfall(pts, coast);
      if (!lf) {
        out.noLandfall++;
        continue;
      }
      const rc = (out.landfall[lf.region] ??= { n: 0, hurricanes: 0, taus: [] });
      rc.n++;
      if (lf.vmax >= 64) rc.hurricanes++;
      rc.taus.push(lf.tau);
    }
    if (coast) out.noLandfall += out.members - formed; // never formed: never landed
    return out;
  });
}

export const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};
